import { AlertTriangle, ExternalLink, Globe, Monitor, RotateCw, Smartphone } from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { PreflightError, SiteDetail } from "../../../api/types";
import { preflightErrorText } from "./hostLabels";
import {
  embeddable,
  PREVIEW_DEVICES,
  PREVIEW_LOAD_TIMEOUT_MS,
  type PreviewDevice,
} from "./hostPreview";

/**
 * host 模式替代 `SitePreview` 的那张卡(A.57;1009 E 线改成嵌入真实站点):
 *  - 没发布过:「发布后在宿主查看」+ 说明,不画假预览;
 *  - 发布过:用 iframe 嵌入 `site.previewUrl`(宿主页面无 X-Frame-Options / CSP,10-09 实测可嵌),桌面 / 手机两档宽度,
 *    按卡片宽度等比缩放;明说「这里显示的是线上版本,草稿改动发布后才会出现」;加载失败(超时)给「打开站点」兜底。
 * ★ iframe 带 sandbox(允许脚本 / 同源 / 新开窗,不许顶层跳转与提交表单 —— 预览里点留资表单不该真提交线索)。
 * ★ 版本号变了(刚发布完)自动重载;另有「刷新」按钮。
 */
export function HostPreviewCard({
  site,
}: {
  site: Pick<SiteDetail, "previewUrl" | "currentVersionNo" | "status">;
}) {
  // 1010:有版本号(含一键生成发的预览,站点仍是草稿态)就嵌入预览;归档站不嵌
  const published = site.currentVersionNo != null && site.status !== "archived";
  return (
    <section
      aria-label="站点预览"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      {published ? (
        <LivePreview url={site.previewUrl} versionNo={site.currentVersionNo} />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Globe className="h-4 w-4 text-primary" aria-hidden />
              发布后在宿主查看
            </h2>
            <p className="text-xs text-muted-foreground">
              站点由宿主按模板渲染,这里没有草稿预览;发布一次后这里会显示线上站点。
            </p>
          </div>
          <Button size="sm" variant="outline" disabled title="发布后可预览">
            <ExternalLink />
            打开站点
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * 真实站点 iframe 预览(桌面 / 手机两档)。1010 预览调整页也用它:`heading` / `note` 换掉「线上 v{n}」那两行,
 * `reloadSignal` 变了就重载(更新预览任务完成后)。
 */
export function LivePreview({
  url,
  versionNo,
  heading,
  note,
  reloadSignal = 0,
  fill = false,
  footnote,
}: {
  /** 1011 算法备案:说明行下方的附注(预览调整页放「内容由AI生成」)。 */
  footnote?: ReactNode;
  url: string;
  versionNo: number | null;
  heading?: string;
  note?: string;
  reloadSignal?: number;
  /** 1010 预览调整页:撑满父容器高度(父容器给定高度),iframe 按宽度缩放、高度铺满。 */
  fill?: boolean;
}) {
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState<"loading" | "loaded" | "failed">(
    embeddable(url) ? "loading" : "failed"
  );
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const [boxHeight, setBoxHeight] = useState(0);

  // 每次换地址 / 版本 / 手动刷新:重新计时
  useEffect(() => {
    if (!embeddable(url)) {
      setState("failed");
      return;
    }
    setState("loading");
    const t = window.setTimeout(
      () => setState((s) => (s === "loading" ? "failed" : s)),
      PREVIEW_LOAD_TIMEOUT_MS
    );
    return () => window.clearTimeout(t);
  }, [url, versionNo, reloadKey, reloadSignal]);

  const measure = useCallback(() => {
    setBoxWidth(boxRef.current?.clientWidth ?? 0);
    setBoxHeight(boxRef.current?.clientHeight ?? 0);
  }, []);
  useEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined" || !boxRef.current) return;
    const ro = new ResizeObserver(measure);
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, [measure]);

  const spec = PREVIEW_DEVICES[device];
  // 量不到宽度(首帧 / 测试环境)按不缩放画;手机档最多 1:1,不放大
  const scale = boxWidth > 0 ? Math.min(1, boxWidth / spec.width) : 1;
  const frameHeight =
    fill && boxHeight > 0 ? Math.round(boxHeight) : Math.round(spec.height * scale);
  const innerHeight = fill && boxHeight > 0 ? Math.round(boxHeight / scale) : spec.height;
  const Wrap = fill ? "div" : Fragment;
  const wrapProps = fill ? { className: "flex h-full min-h-0 flex-col gap-3" } : {};

  return (
    <Wrap {...wrapProps}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Globe className="h-4 w-4 text-primary" aria-hidden />
            {heading ?? `线上 v${versionNo}`}
          </h2>
          <p className="text-xs text-muted-foreground">
            {note ?? "这里显示的是线上版本,草稿改动发布后才会出现。"}
          </p>
          {footnote}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="预览宽度"
            className="flex overflow-hidden rounded-md border"
          >
            {(Object.keys(PREVIEW_DEVICES) as PreviewDevice[]).map((d) => {
              const Icon = d === "desktop" ? Monitor : Smartphone;
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={device === d}
                  onClick={() => setDevice(d)}
                  className={cn(
                    "flex items-center gap-1 px-2.5 py-1 text-xs transition-colors",
                    device === d
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-muted-foreground hover:bg-muted"
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {PREVIEW_DEVICES[d].label}
                </button>
              );
            })}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={!embeddable(url)}
          >
            <RotateCw />
            刷新
          </Button>
          {state !== "failed" && <OpenSiteButton url={url} />}
        </div>
      </div>

      <div ref={boxRef} className={cn("w-full", fill && "min-h-0 flex-1")}>
        {state === "failed" ? (
          <div
            role="status"
            className={cn(
              "flex flex-col items-center justify-center gap-2 border border-dashed border-border bg-muted/40 px-4 py-10 text-center",
              CARD_WALL_RADIUS
            )}
          >
            <p className="text-sm text-foreground">站点没能在这里显示出来</p>
            <p className="text-xs text-muted-foreground">
              可能是网络或站点暂时打不开;直接打开站点看看。
            </p>
            <OpenSiteButton url={url} />
          </div>
        ) : (
          <div
            className={cn(
              "relative mx-auto overflow-hidden border border-border bg-background",
              device === "mobile" ? "rounded-2xl" : "rounded-md"
            )}
            style={{ width: Math.round(spec.width * scale), height: frameHeight }}
          >
            <iframe
              key={`${url}|${versionNo}|${reloadKey}|${reloadSignal}`}
              title="站点线上预览"
              src={url}
              referrerPolicy="no-referrer-when-downgrade"
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              onLoad={() => setState("loaded")}
              onError={() => setState("failed")}
              style={{
                width: spec.width,
                height: innerHeight,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                border: 0,
              }}
            />
            {state === "loading" && (
              <div
                aria-live="polite"
                className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/70 text-xs text-muted-foreground"
              >
                正在加载线上站点…
              </div>
            )}
          </div>
        )}
      </div>
    </Wrap>
  );
}

function OpenSiteButton({ url }: { url: string }) {
  if (!embeddable(url)) {
    return (
      <Button size="sm" variant="outline" disabled title="站点地址暂不可用">
        <ExternalLink />
        打开站点
      </Button>
    );
  }
  return (
    <Button asChild size="sm">
      <a href={url} target="_blank" rel="noopener noreferrer">
        <ExternalLink />
        打开站点
      </a>
    </Button>
  );
}

/** 发布体检的阻断项(A.50):逐条列出,带 token 的可点定位到节点。 */
export function HostPreflightBlockers({
  errors,
  onLocate,
}: {
  errors: PreflightError[];
  onLocate: (token: string) => void;
}) {
  if (errors.length === 0) return null;
  return (
    <section
      role="alert"
      aria-label="发布前要先改的地方"
      className={cn(
        "space-y-2 border border-destructive/50 bg-destructive/10 p-4",
        CARD_WALL_RADIUS
      )}
    >
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden />有 {errors.length}{" "}
        处要先改,才能发布
      </h2>
      <ul className="space-y-1 text-xs text-foreground">
        {errors.map((e, i) => (
          <li key={`${e.token ?? e.path ?? ""}-${i}`}>
            {e.token ? (
              <button
                type="button"
                className="text-left underline-offset-2 hover:underline"
                onClick={() => onLocate(e.token!)}
              >
                {preflightErrorText(e)}
              </button>
            ) : (
              preflightErrorText(e)
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
